import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, ArrowLeft, Loader2, Mail } from "lucide-react";
import { API } from "@/lib/checkout";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/1d2d92c2-e224-4ccb-9ac5-35b11019ccba/images/fbd426ad446c83ddac24872cbbb6eea81a734cb73707db7a16e77f29d6aee64f.png";

const EBOOKS = [
  "The Ultimate Faceless Marketing Guide",
  "Faceless Instagram Marketing",
  "Faceless TikTok Marketing",
  "Faceless YouTube Automation",
  "The Faceless Reels Guide Marketing",
  "10 Step Checklist for Successful Instagram Reels",
  "Content Prompt Playbook",
  "Faceless Content Vault (100 hooks, 90 days)",
  "Faceless Instagram Themepages",
  "Idea to Income: Digital Product Planner Guide",
  "300 Viral Hooks & Stories That Sell",
];

export default function ThankYou() {
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demo = params.get("demo");
    const token = params.get("token") || params.get("order");

    const capture = async () => {
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const { data } = await axios.post(`${API}/paypal/capture-order`, { order_id: token });
        setIsDemo(!!data.demo);
        setStatus(data.status === "COMPLETED" ? "success" : "error");
      } catch (e) {
        console.error(e);
        setStatus("error");
      }
    };

    if (demo) setIsDemo(true);
    capture();
  }, []);

  const handleDownload = () => {
    toast.info("Your ebook files will be delivered here once the store owner uploads them.");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain relative">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-16">
          <img src={LOGO} alt="logo" className="w-9 h-9 object-contain" />
          <span className="font-serif-display text-lg tracking-[0.25em] font-semibold">
            THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
          </span>
        </div>

        {status === "processing" && (
          <div data-testid="thankyou-processing" className="flex flex-col items-center text-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
            <p className="mt-6 text-lg text-zinc-400">Confirming your payment…</p>
          </div>
        )}

        {status === "error" && (
          <div data-testid="thankyou-error" className="flex flex-col items-center text-center py-24">
            <h1 className="font-serif-display text-3xl font-semibold">We couldn't confirm your payment</h1>
            <p className="mt-4 text-zinc-400 max-w-md">
              If you were charged, please contact support. Otherwise, try again.
            </p>
            <a href="/" className="btn-gold mt-10 inline-flex items-center gap-2 px-8 py-4 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to store
            </a>
          </div>
        )}

        {status === "success" && (
          <motion.div
            data-testid="thankyou-success"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/5">
                <CheckCircle2 className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <span className="mt-6 text-sm uppercase tracking-[0.25em] text-[#D4AF37]">Payment successful</span>
              <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4">
                Welcome to your faceless empire
              </h1>
              <p className="mt-6 text-lg text-zinc-400 max-w-xl">
                Thank you for your purchase! Your complete 11-ebook Faceless Marketing Bundle
                is ready below. Download each guide and start building today.
              </p>

              {isDemo && (
                <div className="mt-8 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-5 py-3 text-sm text-[#D4AF37]">
                  Demo mode — connect live PayPal keys to accept real payments.
                </div>
              )}
            </div>

            <div className="mt-16">
              <h2 className="font-serif-display text-2xl font-semibold mb-6">Your Downloads</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EBOOKS.map((title, i) => (
                  <button
                    key={i}
                    data-testid={`download-item-${i}`}
                    onClick={handleDownload}
                    className="group flex items-center justify-between gap-4 bg-[#121212] border border-zinc-800 p-5 text-left hover:border-[#D4AF37]/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-serif-display text-lg text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-medium">{title}</span>
                    </div>
                    <Download className="w-5 h-5 text-zinc-600 group-hover:text-[#D4AF37] transition-colors shrink-0" />
                  </button>
                ))}
              </div>

              <div className="mt-10 flex items-center gap-3 text-sm text-zinc-500 border-t border-zinc-900 pt-8">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                A copy of your download links has also been reserved for your email.
              </div>

              <a href="/" className="inline-flex items-center gap-2 mt-10 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to store
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
